/**
 * The credential card is the printable handout HR gives to a new field user.
 *
 * Deliberately minimal: no photo, no QR. The two-part provisioning code is
 * the only thing the worker has to act on, so it gets the whole stage — and
 * a card this small prints several to a page.
 */
import { useRef, useState } from 'react';
import { Check, Copy, Link2, Printer, X } from 'lucide-react';

export type CredentialCardProps = {
  tenantName: string;
  fullName: string;
  employeeId: string;
  code: string;
  expiresAt: string;
  /** Short shareable URL for this card, e.g. `https://app.example.com/card/EMP-001`. */
  shareUrl?: string;
  onClose: () => void;
};

export function CredentialCard({
  tenantName,
  fullName,
  employeeId,
  code,
  expiresAt,
  shareUrl,
  onClose,
}: CredentialCardProps) {
  const printableRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  const copy = async (value: string, what: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      /* clipboard blocked (insecure origin / denied) — the value is on screen anyway */
    }
  };

  const printCard = () => {
    const node = printableRef.current;
    if (!node) return;
    const win = window.open('', 'cred-card', 'width=420,height=560');
    if (!win) return;
    win.document.write(
      `<!doctype html><html><head><title>Setup card · ${employeeId}</title>` +
        `<style>
           body{font-family:system-ui,-apple-system,sans-serif;margin:0;padding:24px;color:#0b0c10}
           .pc{width:320px;border:1px solid #d8dce3;border-radius:12px;padding:18px}
           .pc-org{font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#5b6472}
           .pc-name{margin-top:10px;font-size:17px;font-weight:600}
           .pc-emp{margin-top:2px;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#5b6472}
           .pc-label{margin-top:16px;font-size:9px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#5b6472}
           .pc-code{margin-top:5px;padding:11px 8px;text-align:center;background:#f4f6f9;border:1px solid #e4e7ec;border-radius:8px;
                    font-family:ui-monospace,Menlo,Consolas,monospace;font-size:23px;font-weight:700;letter-spacing:.13em}
           .pc-steps{margin:14px 0 0;padding-left:16px;font-size:11px;line-height:1.7;color:#3a4150}
           .pc-exp{margin-top:12px;font-size:10px;color:#5b6472}
         </style></head><body>${node.innerHTML}</body></html>`,
    );
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 250);
  };

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="w-full max-w-xs rounded-xl border border-border bg-surface p-4 text-foreground shadow-elevated">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Setup card</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Printable region — class names match the print stylesheet above. */}
        <div ref={printableRef}>
          <div className="pc rounded-lg border border-border bg-background p-4">
            <div className="pc-org text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              {tenantName}
            </div>
            <div className="pc-name mt-2.5 text-base font-semibold leading-tight">{fullName}</div>
            <div className="pc-emp mt-0.5 font-mono text-[11px] text-muted-foreground">
              {employeeId}
            </div>

            <div className="pc-label mt-4 text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Provisioning code
            </div>
            <div className="pc-code mt-1.5 rounded-lg border border-border bg-muted/50 px-2 py-2.5 text-center font-mono text-xl font-bold tracking-[0.13em]">
              {code}
            </div>

            <ol className="pc-steps mt-3 list-decimal pl-4 text-[11px] leading-relaxed text-muted-foreground">
              <li>Install Fleet Iris from the app store.</li>
              <li>Tap "Get Started" and enter this code.</li>
              <li>Set a 4-digit PIN.</li>
            </ol>

            <p className="pc-exp mt-3 text-[10px] text-muted-foreground">
              Expires {new Date(expiresAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="mt-3 flex gap-1.5">
          <button
            onClick={() => copy(code, 'code')}
            className="btn-secondary btn-sm flex-1"
            title="Copy provisioning code"
          >
            {copied === 'code' ? (
              <Check className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {copied === 'code' ? 'Copied' : 'Code'}
          </button>
          {shareUrl && (
            <button
              onClick={() => copy(shareUrl, 'link')}
              className="btn-secondary btn-sm flex-1"
              title={shareUrl}
            >
              {copied === 'link' ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Link2 className="h-3.5 w-3.5" />
              )}
              {copied === 'link' ? 'Copied' : 'Link'}
            </button>
          )}
          <button onClick={printCard} className="btn-primary btn-sm flex-1" title="Print card">
            <Printer className="h-3.5 w-3.5" />
            Print
          </button>
        </div>
      </div>
    </div>
  );
}
